import { ShellSnippet } from '@/shared/components/ShellSnippet'
import type { OrgItem } from '@/lib/apiTypes'
import { providerRegistryHost, providerTokenEnvVar } from '@/lib/format'
import { s } from '@/features/integrations/guideStyles'
import type { GuideId } from '@/features/integrations/guides'

// ── Terramantle CLI: Signing In ──────────────────────────────────────────────
export function CliAuthGuide({ org, onSwitchGuide }: { org: OrgItem | null; onSwitchGuide: (id: GuideId) => void }) {
  const orgSlug = org?.slug ?? '<org-slug>'
  const regHost = providerRegistryHost(org?.slug)
  const tokenVar = providerTokenEnvVar(regHost)

  return (
    <div>
      <h3 style={s.guideTitle}>Signing In</h3>
      <p style={s.para}>
        One auth model for humans and pipelines. The mode is auto-detected from the environment, so the same
        commands work on your laptop and on a runner. Nothing vendor-specific is baked into the binary - the
        OIDC issuer, audience and scopes are fetched from the deployment at runtime.
      </p>

      <h3 style={s.subTitle}>Sign in</h3>
      <ShellSnippet code={`terramantle auth login     # device flow: opens a URL, enter the code
terramantle auth whoami    # identity, org(s), token type + expiry
terramantle auth logout`} language="bash" />
      <p style={{ ...s.para, marginTop: 10 }}>
        The access and refresh tokens are stored in your OS keyring, never in a config file. The verification
        URL and code are printed to stderr, so they stay out of anything you pipe.
      </p>

      <h3 style={s.subTitle}>Exposing the credentials to your shell</h3>
      <p style={s.para}>
        <code style={s.code}>auth env</code> prints shell exports for <code style={s.code}>eval</code>, and{' '}
        <code style={s.code}>auth token</code> prints the bearer on its own (all narration goes to stderr, so
        the capture stays clean):
      </p>
      <ShellSnippet code={`# TERRAMANTLE_TOKEN, _API_URL, and _ORG when it resolves
eval "$(terramantle auth env)"

# ...plus TF_HTTP_PASSWORD and ${tokenVar}
eval "$(terramantle auth env --terraform)"

# Other shells get shell-correct quoting
terramantle auth env --format fish
terramantle auth env --format powershell
terramantle auth env --format json

# Persist a mode-600 dotenv instead of evaluating it
terramantle auth env --write --path ~/.config/terramantle/token.env

# Just the bearer
export TERRAMANTLE_TOKEN=$(terramantle auth token)`} language="bash" />
      <p style={{ ...s.para, marginTop: 10 }}>
        Both commands export <code style={s.code}>TERRAMANTLE_TOKEN</code>, which would otherwise put the{' '}
        <em>next</em> invocation into raw-token mode holding the previous bearer. They refresh a stale one from
        the keyring instead, so re-running the <code style={s.code}>eval</code> after your access token expires
        just works. A session that genuinely cannot be refreshed exits <code style={s.code}>5</code> and tells
        you to run <code style={s.code}>auth login</code>, rather than handing Terraform a dead bearer.
      </p>

      <h3 style={s.subTitle}>Auth modes</h3>
      <p style={s.para}>
        Auto-detected in this order; override with <code style={s.code}>--auth-mode</code> or{' '}
        <code style={s.code}>TERRAMANTLE_AUTH_MODE</code>.
      </p>
      <ul style={s.list}>
        <li><code style={s.code}>github</code> - on <code style={s.code}>GITHUB_ACTIONS=true</code>: the runner's
          ambient OIDC id-token, which needs <code style={s.code}>permissions: id-token: write</code>. No stored secret.</li>
        <li><code style={s.code}>gitlab</code> - on <code style={s.code}>GITLAB_CI=true</code>: the id-token from{' '}
          <code style={s.code}>TERRAMANTLE_ID_TOKEN</code>, declared with an <code style={s.code}>id_tokens:</code> block.</li>
        <li><code style={s.code}>device</code> - an interactive terminal: the OIDC device flow used by{' '}
          <code style={s.code}>auth login</code>.</li>
        <li><code style={s.code}>client</code> - when <code style={s.code}>TERRAMANTLE_CLIENT_ID</code> and{' '}
          <code style={s.code}>TERRAMANTLE_CLIENT_SECRET</code> are set: a bot client-credentials grant.</li>
        <li><code style={s.code}>token</code> - when <code style={s.code}>TERRAMANTLE_TOKEN</code> is set: used
          verbatim, skipping every flow. An expired one falls through to the keyring and is refreshed.</li>
      </ul>

      <h3 style={s.subTitle}>Contexts</h3>
      <p style={s.para}>
        Contexts name an org + workspace pair so you stop repeating <code style={s.code}>--org</code> and{' '}
        <code style={s.code}>--workspace</code>.
      </p>
      <ShellSnippet code={`terramantle context set ${orgSlug}-prod --org ${orgSlug} --workspace prod
terramantle context set ${orgSlug}-stg  --org ${orgSlug} --workspace staging

terramantle context ls
terramantle context use ${orgSlug}-prod
terramantle context current

terramantle config view    # the effective resolved config, secrets redacted`} language="bash" />

      <h3 style={s.subTitle}>Where settings come from</h3>
      <p style={s.para}>
        Config lives at <code style={s.code}>~/.config/terramantle/config.toml</code> and holds contexts only -
        never secrets, which stay in the OS keyring. Values resolve highest-wins:
      </p>
      <ol style={s.list}>
        <li>An explicit global flag (<code style={s.code}>--org</code>, <code style={s.code}>--workspace</code>,{' '}
          <code style={s.code}>--api-url</code>, <code style={s.code}>--context</code>, <code style={s.code}>-o</code>)</li>
        <li>A <code style={s.code}>TERRAMANTLE_*</code> environment variable</li>
        <li>The selected context in the config file</li>
        <li>A token-derived server default (single-org humans only)</li>
        <li>Otherwise a precise error, rather than a guess</li>
      </ol>

      <h3 style={s.subTitle}>Notes</h3>
      <ul style={s.list}>
        <li>For a human token the org defaults from the server when you belong to exactly one org. For CI OIDC
          and bot tokens the org is resolved from repo trust and there is no org lookup, so{' '}
          <code style={s.code}>--org</code> / <code style={s.code}>TERRAMANTLE_ORG</code> is{' '}
          <strong>required</strong> in CI - see{' '}
          <button style={s.linkBtn} onClick={() => onSwitchGuide('cli-ci')}>Using in CI</button>.</li>
        <li><code style={s.code}>device</code> login needs the deployment to have a public device-flow client
          provisioned. If it does not, <code style={s.code}>auth login</code> says so - use a bot token or CI
          OIDC in the meantime.</li>
        <li>Before any auth the CLI reads{' '}
          <code style={s.code}>{'{api-url}'}/.well-known/terramantle-cli.json</code> for the issuer, audience and
          scopes, then the issuer's own <code style={s.code}>openid-configuration</code>.</li>
      </ul>
    </div>
  )
}
